import { useState, useRef, useEffect, ChangeEvent, KeyboardEvent } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import {
  Sparkles, 
  Send, 
  Loader2, 
  MessageSquare, 
  FileText, 
  Calendar, 
  Search, 
  Bot,
  User,
  Copy,
  Download,
  Trash2,
  Settings,
  ThumbsUp,
  ThumbsDown
} from 'lucide-react';
import { useAI, type AITaskType } from '@/hooks/useAI';
import { toast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Case } from '@/types/case';

// Interfaces e constantes permanecem as mesmas
interface ChatMessage {
  id: string;
  type: 'user' | 'ai';
  content: string;
  timestamp: Date;
  taskType: AITaskType;
  interactionId?: string; // Adicionar ID da interação para feedback
  feedbackGiven?: boolean; // Para controlar se o feedback já foi dado
}

interface AdvancedAIChatProps {
  context?: string;
  initialTaskType?: AITaskType;
  title?: string;
  placeholder?: string;
  fullScreen?: boolean;
  caseId?: string; // Nova prop para o ID do processo
  initialPrompt?: string; // Nova prop para prompt inicial
}

import { promptTemplates } from '@/lib/aiPromptTemplates';


// ============================================================================
// PASSO 1: CRIAR O COMPONENTE DE INTERFACE SEPARADO (A APARÊNCIA)
// Este componente apenas exibe a UI e recebe toda a lógica via props.
// Ele é definido FORA do componente principal.
// ============================================================================
interface ChatInterfaceProps {
  title: string;
  placeholder: string;
  fullScreen: boolean;
  taskType: AITaskType;
  messages: ChatMessage[];
  loading: boolean;
  prompt: string;
  isSettingsOpen: boolean;
  textareaRef: React.RefObject<HTMLTextAreaElement>;
  messagesEndRef: React.RefObject<HTMLDivElement>;
  setIsSettingsOpen: (isOpen: boolean) => void;
  setTaskType: (task: AITaskType) => void;
  handleInputChange: (e: ChangeEvent<HTMLTextAreaElement>) => void;
  handleKeyDown: (e: KeyboardEvent<HTMLTextAreaElement>) => void;
  handleSubmit: () => void;
  useTemplate: (template: string) => void;
  copyMessage: (content: string) => void;
  clearChat: () => void;
  exportChat: () => void;
  getTaskTypeLabel: (type: AITaskType) => string;
}

const ChatInterface = ({
  title, placeholder, fullScreen, taskType, messages, loading, prompt, isSettingsOpen,
  textareaRef, messagesEndRef,
  setIsSettingsOpen, setTaskType, handleInputChange, handleKeyDown, handleSubmit, useTemplate, copyMessage, clearChat, exportChat, getTaskTypeLabel
}: ChatInterfaceProps) => {
  // A função para cor da badge pode ficar aqui ou ser passada por prop.
  // Para simplicidade, vamos mantê-la aqui, pois é puramente visual.
  const getTaskTypeColor = (type: AITaskType) => {
    const colors = {
      case_analysis: 'bg-primary text-primary-foreground',
      document_generation: 'bg-secondary text-secondary-foreground',
      deadline_management: 'bg-orange-500 text-white',
      legal_research: 'bg-blue-500 text-white',
      general: 'bg-muted text-muted-foreground'
    };
    return colors[type];
  };

  return (
    <div className={`flex flex-col ${fullScreen ? 'h-screen' : 'h-[600px]'}`}>
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b">
        <div className="flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-primary" />
          <h3 className="font-semibold">{title}</h3>
          <Badge className={getTaskTypeColor(taskType)}>
            {getTaskTypeLabel(taskType)}
          </Badge>
        </div>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="icon" onClick={exportChat} disabled={messages.length === 0} title="Exportar Chat">
            <Download className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="icon" onClick={clearChat} disabled={messages.length === 0} title="Limpar Chat">
            <Trash2 className="w-4 h-4" />
          </Button>
          <Dialog open={isSettingsOpen} onOpenChange={setIsSettingsOpen}>
            <DialogTrigger asChild>
              <Button variant="ghost" size="icon" title="Configurações">
                <Settings className="w-4 h-4" />
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Configurações do Chat</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div>
                  <label className="text-sm font-medium">Tipo de Assistente</label>
                  <Select value={taskType} onValueChange={(value) => setTaskType(value as AITaskType)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="general">Assistente Geral</SelectItem>
                      <SelectItem value="case_analysis">Análise de Processo</SelectItem>
                      <SelectItem value="document_generation">Geração de Documentos</SelectItem>
                      <SelectItem value="deadline_management">Gestão de Prazos</SelectItem>
                      <SelectItem value="legal_research">Pesquisa Jurídica</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">Templates de Prompts</label>
                  <div className="grid grid-cols-1 gap-2">
                    {promptTemplates[taskType].map((template) => (
                      <Button key={template} variant="outline" onClick={() => { useTemplate(template); setIsSettingsOpen(false); }} className="text-left justify-start h-auto p-2 w-full">
                        {template}
                      </Button>
                    ))}
                  </div>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Messages */}
      <ScrollArea className="flex-1 p-4">
        <div className="space-y-4">
          {messages.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Bot className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>Inicie uma conversa com o assistente IA</p>
              <p className="text-sm">Escolha um template ou faça sua pergunta</p>
            </div>
          ) : (
            messages.map((message) => (
              <div key={message.id} className={`flex gap-3 ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`flex gap-3 max-w-[80%] ${message.type === 'user' ? 'flex-row-reverse' : 'flex-row'}`}>
                  <div className={`w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center ${message.type === 'user' ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}>
                    {message.type === 'user' ? <User className="w-4 h-4" /> : <Bot className="w-4 h-4" />}
                  </div>
                  <div className={`rounded-lg p-3 ${message.type === 'user' ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}>
                    <div className="whitespace-pre-wrap text-sm">{message.content}</div>
                    <div className="flex items-center justify-between mt-2 gap-2">
                      <span className="text-xs opacity-70">{message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                      <Button variant="ghost" size="icon" onClick={() => copyMessage(message.content)} className="h-6 w-6 p-1 opacity-50 hover:opacity-100" title="Copiar">
                        <Copy className="w-3 h-3" />
                      </Button>
                    </div>
                    {message.type === 'ai' && message.interactionId && !message.feedbackGiven && (
                      <div className="flex items-center gap-2 mt-2">
                        <span className="text-xs text-muted-foreground">Avalie a resposta:</span>
                        <Button variant="ghost" size="icon" onClick={() => console.log('Feedback positivo')} className="h-6 w-6 p-1 text-green-500 hover:bg-green-100" title="Bom">
                          <ThumbsUp className="w-3 h-3" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => console.log('Feedback negativo')} className="h-6 w-6 p-1 text-red-500 hover:bg-red-100" title="Ruim">
                          <ThumbsDown className="w-3 h-3" />
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
          {loading && (
            <div className="flex gap-3 justify-start">
              <div className="w-8 h-8 rounded-full bg-muted text-muted-foreground flex items-center justify-center flex-shrink-0"><Bot className="w-4 h-4" /></div>
              <div className="bg-muted rounded-lg p-3"><div className="flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /><span className="text-sm">Processando...</span></div></div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
      </ScrollArea>

      <Separator />

      {/* Input */}
      <div className="p-4 space-y-3 bg-background">
        <div className="flex gap-2 flex-wrap">
          {promptTemplates[taskType].slice(0, 2).map((template) => (
            <Button key={template} variant="outline" size="sm" onClick={() => useTemplate(template)} className="text-xs h-auto py-1 px-2">{template}</Button>
          ))}
        </div>
        <div className="flex gap-2 items-end">
          <Textarea
            ref={textareaRef}
            value={prompt}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            rows={1}
            className="resize-none flex-1 text-sm p-2"
          />
          <Button onClick={handleSubmit} disabled={loading || !prompt.trim()} size="icon" className="h-10 w-10 flex-shrink-0">
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
          </Button>
        </div>
      </div>
    </div>
  );
};


// ============================================================================
// PASSO 2: SIMPLIFICAR O COMPONENTE PRINCIPAL (A LÓGICA)
// Agora ele apenas gerencia a lógica e passa tudo para o ChatInterface.
// ============================================================================
export const AdvancedAIChat = ({
  context,
  initialTaskType = 'general',
  title = "Assistente IA Jurídico Avançado",
  placeholder = "Digite sua pergunta ou solicite ajuda...",
  fullScreen = false,
  initialPrompt,
  caseId // Destrutura a nova prop caseId
}: AdvancedAIChatProps) => {
  

  

  const [caseContext, setCaseContext] = useState<string | undefined>(undefined);

  useEffect(() => {
    const fetchCaseDetails = async () => {
      if (!caseId) {
        setCaseContext(undefined);
        return;
      }
      try {
        const { data, error } = await supabase.from('cases').select(`*,
          clients:client_id (name, email)
        `).eq('id', caseId).single();

        if (error) throw error;

        if (data) {
          const caseData = data as any;
          const formattedContext = `
            Processo ID: ${caseData.id}
            Número CNJ: ${caseData.case_number_cnj || 'N/A'}
            Título: ${caseData.title || 'N/A'}
            Tipo: ${caseData.case_type || 'N/A'}
            Status: ${caseData.status || 'N/A'}
            Prioridade: ${caseData.priority || 'N/A'}
            Prazo: ${caseData.deadline ? new Date(caseData.deadline).toLocaleDateString('pt-BR') : 'N/A'}
            Descrição: ${caseData.description || 'N/A'}
            Cliente: ${caseData.clients?.name || 'N/A'}
            Email do Cliente: ${caseData.clients?.email || 'N/A'}
            Tribunal: ${caseData.court || 'N/A'}
            Data de Autuação: ${caseData.filing_date ? new Date(caseData.filing_date).toLocaleDateString('pt-BR') : 'N/A'}
            Última Atualização Datajud: ${caseData.datajud_json?.dataHoraUltimaAtualizacao ? new Date(caseData.datajud_json.dataHoraUltimaAtualizacao).toLocaleDateString('pt-BR') : 'N/A'}
            Movimentações Recentes (Datajud):
            ${caseData.datajud_json?.movimentacoes?.slice(0, 5).map(m => `  - ${m.dataHora ? new Date(m.dataHora).toLocaleDateString('pt-BR') : ''}: ${m.nome}`).join('\n') || 'Nenhuma movimentação recente.'}
          `;
          setCaseContext(formattedContext);
        }
      }
      catch (err: any) {
        console.error("Erro ao buscar detalhes do processo para contexto da IA:", err);
        if (typeof toast === 'function') {
          toast({ title: "Erro", description: "Não foi possível carregar o contexto do processo.", variant: "destructive" });
        }
        setCaseContext(undefined);
      }
    };

    fetchCaseDetails();
  }, [caseId, toast]);

  const currentContext = caseContext || context; // Prioriza o contexto do processo se disponível

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [prompt, setPrompt] = useState(initialPrompt || ''); // Use initialPrompt aqui
  const [taskType, setTaskType] = useState<AITaskType>(initialTaskType);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const { loading, askAI } = useAI();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);


  useEffect(() => {
    if (initialPrompt) {
      setPrompt(initialPrompt);
      textareaRef.current?.focus();
    }
  }, [initialPrompt]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const getTaskTypeLabel = (type: AITaskType) => {
    const labels = {
      case_analysis: 'Análise de Processo',
      document_generation: 'Geração de Documentos',
      deadline_management: 'Gestão de Prazos',
      legal_research: 'Pesquisa Jurídica',
      general: 'Assistente Geral'
    };
    return labels[type];
  };

  const handleSubmit = async () => {
    if (!prompt.trim() || loading) return;
    const userMessage: ChatMessage = { id: Date.now().toString(), type: 'user', content: prompt, timestamp: new Date(), taskType };
    setMessages(prev => [...prev, userMessage]);
    const currentPrompt = prompt;
    setPrompt('');
    try {
      const aiResult = await askAI({ prompt: currentPrompt, context: currentContext, task_type: taskType });
      if (aiResult?.response) {
        const aiMessage: ChatMessage = { 
          id: (Date.now() + 1).toString(), 
          type: 'ai', 
          content: aiResult.response, 
          timestamp: new Date(), 
          taskType, 
          interactionId: aiResult.interactionId || undefined 
        };
        setMessages(prev => [...prev, aiMessage]);
      }
    } catch (error) {
      toast({ title: "Erro na IA", description: error instanceof Error ? error.message : "Não foi possível processar sua solicitação.", variant: "destructive" });
      setPrompt(currentPrompt); // Devolve o prompt ao usuário em caso de erro
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };
  
  const handleInputChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setPrompt(e.target.value);
  };
  
  const useTemplate = (template: string) => {
    setPrompt(template);
    textareaRef.current?.focus();
  };

  const copyMessage = (content: string) => {
    navigator.clipboard.writeText(content);
    toast({ title: "Copiado!", description: "Mensagem copiada para a área de transferência" });
  };
  
  const clearChat = () => {
    setMessages([]);
    toast({ title: "Chat limpo", description: "Todas as mensagens foram removidas" });
  };

  const handleFeedback = async (messageId: string, interactionId: string, score: number) => {
    try {
      // Temporarily disabled until ai_interactions table is created
      console.log('Feedback recorded:', { messageId, interactionId, score });
      
      // Update the message to mark feedback as given
      setMessages(prev => prev.map(msg => 
        msg.id === messageId ? { ...msg, feedbackGiven: true } : msg
      ));

      setMessages(prevMessages =>
        prevMessages.map(msg =>
          msg.id === messageId ? { ...msg, feedbackGiven: true } : msg
        )
      );
      toast({ title: "Feedback registrado!", description: "Obrigado por sua avaliação." });
    } catch (error) {
      console.error("Erro ao registrar feedback:", error);
      toast({ title: "Erro", description: "Não foi possível registrar seu feedback.", variant: "destructive" });
    }
  };

  const exportChat = () => {
    if (messages.length === 0) return;
    const chatText = messages.map(msg => `[${msg.timestamp.toLocaleString()}] ${msg.type === 'user' ? 'Você' : 'IA'}: ${msg.content}`).join('\n\n');
    const blob = new Blob([chatText], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `chat-ai-${new Date().toISOString().split('T')[0]}.txt`;
    a.click();
    URL.revokeObjectURL(url);
    toast({ title: "Chat exportado", description: "Arquivo baixado com sucesso" });
  };

  // Objeto com todas as props para passar para o componente de UI
  const chatInterfaceProps = {
    title, placeholder, fullScreen, taskType, messages, loading, prompt, isSettingsOpen,
    textareaRef, messagesEndRef,
    setIsSettingsOpen, setTaskType, handleInputChange, handleKeyDown, handleSubmit, useTemplate, copyMessage, clearChat, exportChat, getTaskTypeLabel,
    handleFeedback // Passar a função de feedback
  };

  // O componente da UI (ChatInterface) é chamado aqui, passando tudo via props.
  if (fullScreen) {
    return <ChatInterface {...chatInterfaceProps} />;
  }

  return (
    <Card className="w-full h-full flex flex-col">
      <CardContent className="p-0 flex-grow">
        <ChatInterface {...chatInterfaceProps} />
      </CardContent>
    </Card>
  );
};